import type { AmhsLiveInventory } from '../types/amhsStream';
import { liveStockerColumnSplit } from '../features/simulation/liveStockerInventory';

type Listener = () => void;

function stockerFingerprint(inventory: AmhsLiveInventory | null): string {
  if (!inventory?.stockers?.length) return '';
  return inventory.stockers
    .map((stocker) => {
      const columns = liveStockerColumnSplit(stocker);
      return `${stocker.stockerId}:${columns?.raw ?? ''}:${columns?.processed ?? ''}:${stocker.occupancy ?? ''}`;
    })
    .join('|');
}

/**
 * Latest unheld inventory frame from the live stream (Redux mirror). Drives
 * 2D stocker dot authoritative counts during transfers without coupling hooks
 * to Redux subscription timing.
 */
class LiveStreamInventoryStore {
  private raw: AmhsLiveInventory | null = null;
  private fingerprint = '';
  private readonly listeners = new Set<Listener>();

  setRaw(inventory: AmhsLiveInventory | null): void {
    if (this.raw === inventory) return;
    this.raw = inventory;
    this.notifyIfChanged();
  }

  getSnapshot(): AmhsLiveInventory | null {
    return this.raw;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  reset(): void {
    this.raw = null;
    this.fingerprint = '';
    for (const listener of this.listeners) listener();
  }

  resetFingerprint(): void {
    this.fingerprint = stockerFingerprint(this.raw);
  }

  private notifyIfChanged(): void {
    const next = stockerFingerprint(this.raw);
    if (next === this.fingerprint) return;
    this.fingerprint = next;
    for (const listener of this.listeners) listener();
  }
}

export const liveStreamInventoryStore = new LiveStreamInventoryStore();
